import queryString from 'query-string';
import {FC, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router-dom';
import {combineReducers} from 'redux';
import useReducerWithThunk from 'use-reducer-thunk';
import {Segment} from '~src/interfaces/segment.interface';

import {SurveySubjectTypes} from '~src/interfaces/surveys.interfaces';
import {Topic} from '~src/interfaces/topic.interface';
import {GlobalSpinnerContainer} from '~src/modules/heatmap/components/table/ratingTable.sc';
import {AnalysisLineRequest} from '~src/services/api/reporting';
import {Uuid} from '~src/services/uuid';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import {OptionItem} from '~src/shared/selectors/DdSearchContent';
import {RESET_MATRIX} from '~src/store/heatmap/action.types';
import {
  chartDimensionSurveyTopicsAction,
  chartDimensionTopicQuestionsAction,
  fetchLines,
} from '~src/store/spidergraph/actions';
import {getUrlFromObject} from '~src/utils';

import {StoreI} from '~src/rootReducer';
import Header from './components/Header';
import LinesSelector from './components/LinesSelector';
import SpiderGraphSelector from './components/SurveySelector';
import SpiderChart from './components/spider/SpiderChart';
import {dataReducer, DataState, initialDataState} from './data.reducer';
import {
  deselectTopicAction,
  getAttributesAndScales,
  navigationReducer,
  selectAboutSegmentsAction,
  selectAskSegmentsAction,
  selectCyclesAction,
  selectSurveyAction,
  selectSurveyTypeAction,
  selectTopicAction,
  setActiveStepIndex,
  setIncludeSelfAssessmentAction,
} from './navigation.reducer';
import {AnalysisSpiderParams, RichCycle} from './types';

interface OwnProps {
  topicId?: string;
  onUrlParamsChange?: (oldParams: AnalysisSpiderParams, newParams: AnalysisSpiderParams) => void;
}

export type AnalysisProps = RouteComponentProps<never> & OwnProps;

const isUUID = (uuid: string): boolean => {
  try {
    Uuid.fromHexString(uuid);
    return true;
  } catch (e) {
    return false;
  }
};

export function getLineRequests(
  selectedCycles: RichCycle[],
  selectedSegments: Segment[],
  selectedAskSegments: Segment[],
  selectedTopic: string,
  isSelfAssessmentIncluded: boolean,
): AnalysisLineRequest[] {
  const result: Record<string, AnalysisLineRequest> = {};
  const aboutSegmentIds: string[] = selectedSegments.map(s => s.id);
  const askSegmentIds: string[] = selectedAskSegments.map(s => s.id);

  selectedCycles.forEach((selectedCycle) => {
    const surveyId = selectedCycle.survey.id;
    result[surveyId] = result[surveyId] || {
      cycleIds: [],
      surveySubject: selectedCycle.survey.subject,
      surveyId: selectedCycle.survey.id,
      aboutSegmentIds: aboutSegmentIds,
      askSegmentIds: askSegmentIds,
      topicId: selectedTopic,
      isSelfAssessmentIncluded,
    };
    result[surveyId].cycleIds = result[surveyId].cycleIds || [];
    result[surveyId].cycleIds.push(selectedCycle.getId());
  });

  return Object.values(result);
}

interface State {
  data: DataState;
  navigation: AnalysisSpiderParams
}

// TODO: Refactor and convert this into a common component soon
const SpiderGraph: FC<AnalysisProps> = (props) => {
  const initialNavigationState = queryString.parse(props.location.search) as AnalysisSpiderParams;
  initialNavigationState.isSelfAssessmentIncluded =
    String(initialNavigationState.isSelfAssessmentIncluded) === 'true';

  const [state, dispatch] = useReducerWithThunk(
    combineReducers<State>({
      data: dataReducer,
      navigation: navigationReducer,
    }),
    {
      data: initialDataState(),
      navigation: initialNavigationState,
    },
  );

  const analysisDispatch = useDispatch();
  const [selectedAboutSegments, setSelectedAboutSegments] = useState<Segment[]>([]);
  const [selectedAskSegments, setSelectedAskSegments] = useState<Segment[]>([]);

  const [selectedCycles, setSelectedCycles] = useState<RichCycle[]>([]);
  const [autoSelectMostRecentCycle, setAutoSelectMostRecentCycle] = useState<boolean>(false);
  const userCompanyId = useSelector<StoreI, string | undefined>((s) => {
    return s.user.session.company?.uuid;
  });

  const navState = state!.navigation;
  const dataState = state!.data;
  const activeStepIndex = parseInt(String(navState.activeStepIndex ?? 0));

  useEffect(() => {
    dispatch(getAttributesAndScales());
    analysisDispatch({ type: RESET_MATRIX });
    if (navState.survey && navState.surveySubjectType) {
      dispatch(selectSurveyAction(navState.survey, navState.surveySubjectType));
    }
    if (navState.aboutSegments) {
      dispatch(selectAboutSegmentsAction(navState.aboutSegments));
    }
    if (navState.cycles && navState.cycles.length) {
      dispatch(selectCyclesAction(navState.cycles));
    } else {
      setAutoSelectMostRecentCycle(true);
    }
    if (navState.topic) {
      dispatch(selectTopicAction(navState.topic));
    }
  }, []);

  useEffect(() => {
    // on load, auto-select single topics
    if (!navState.topic && dataState.topics && dataState.topics.length === 1) {
      dispatch(selectTopicAction(dataState.topics[0].id));
    }
  }, [dataState.topics]);

  useEffect(() => {
    if (!dataState.topics || !dataState.survey) {
      return;
    }
    const topic = dataState.topics.find(t => t.id === navState.topic);
    if (topic) {
      analysisDispatch(chartDimensionTopicQuestionsAction(topic));
    } else {
      analysisDispatch(chartDimensionSurveyTopicsAction(dataState.survey, dataState.topics));
    }
  }, [dataState.survey, dataState.topics, navState.topic]);

  useEffect(() => {
    // reset segments, for example on survey change
    if (navState.aboutSegments && !navState.aboutSegments.length) {
      setSelectedAboutSegments([]);
    }
  }, [navState.aboutSegments]);

  useEffect(() => {
    if (autoSelectMostRecentCycle && !(navState.cycles || []).length) {
      const mostRecentCycle = RichCycle.getMostRecentCycle(dataState.richCycles ?? []);
      if (mostRecentCycle) {
        setAutoSelectMostRecentCycle(false);
        dispatch(selectCyclesAction([mostRecentCycle.getId()]));
        return;
      }
    }

    setSelectedCycles(
      dataState.richCycles?.filter(r => (navState.cycles || []).indexOf(r.getId()) !== -1) ?? [],
    );
  }, [navState.cycles, dataState.richCycles]);

  useEffect(() => {
    const requestedCycles = selectedCycles.length ? selectedCycles : dataState.richCycles ?? [];

    const missingLines = getLineRequests(
      requestedCycles,
      selectedAboutSegments,
      selectedAskSegments,
      navState.topic || '',
      navState.isSelfAssessmentIncluded || false,
    );
    analysisDispatch(fetchLines(missingLines));
  }, [
    selectedAboutSegments,
    selectedAskSegments,
    selectedCycles,
    navState.topic,
    navState.isSelfAssessmentIncluded,
  ]);

  const onSurveySelectChange = (selectedItem: OptionItem, surveyType: SurveySubjectTypes) => {
    analysisDispatch({ type: RESET_MATRIX });
    setAutoSelectMostRecentCycle(true);
    if (isUUID(selectedItem.id)) {
      dispatch(selectSurveyAction(selectedItem.id, surveyType));
    } else {
      dispatch(selectSurveyTypeAction(surveyType));
    }
    dispatch(setActiveStepIndex(1));
  };

  useEffect(() => {
    props.history.replace({ search: getUrlFromObject(navState) });
  }, [navState]);

  const loadingSurveyRelatedData =
    dataState.loadingAttrs || dataState.loadingScales || dataState.loadingSurvey;

  const onAboutSegmentsSelectionChange = (segments: Segment[]) => {
    dispatch(selectAboutSegmentsAction(segments.map(s => s.id)));
    if (activeStepIndex === 1) {
      dispatch(setActiveStepIndex(2));
    }
    setSelectedAboutSegments(segments);
  };
  const onCyclesSelectionChange = (cycles: RichCycle[]) => {
    dispatch(selectCyclesAction(cycles.map(c => c.getId())));
    dispatch(setActiveStepIndex(3));
  };

  const onTopicSelectionChange = (topic?: Topic) => {
    dispatch(topic ? selectTopicAction(topic.id) : deselectTopicAction());
  };

  const onSelfAssessmentChange = (isIncluded: boolean) => {
    dispatch(setIncludeSelfAssessmentAction(isIncluded));
  };
  const onAskSegmentsSelectionChange = (segments: Segment[]) => {
    const askSegmentIds = segments.map(s => s.id);
    dispatch(selectAskSegmentsAction(askSegmentIds));
    setSelectedAskSegments(segments);
  };

  const chartControlsDataAvailable =
    dataState.richCycles &&
    dataState.richCycles.length > 0 &&
    navState.survey === dataState.richCycles[0]?.survey.id;

  const isPeopleSurveys = state?.navigation.surveySubjectType === SurveySubjectTypes.People;

  if (loadingSurveyRelatedData) {
    return (
      <GlobalSpinnerContainer>
        <LoadingSpinner />
      </GlobalSpinnerContainer>
    );
  }
  const defaultAskSegments = userCompanyId ? [userCompanyId] : [];
  const defaultAboutSegments = userCompanyId && !isPeopleSurveys ? [userCompanyId] : [];
  let initialAboutSegments = defaultAboutSegments;

  if (navState?.aboutSegments?.length) {
    initialAboutSegments = Array.isArray(navState.aboutSegments)
      ? navState.aboutSegments
      : [navState.aboutSegments];
  }

  if (!state) {
    return null;
  }

  const chartControls = chartControlsDataAvailable ? (
    <>
      <LinesSelector
        availableCycles={dataState.richCycles ?? []}
        availableTopics={Object.values(dataState.topics || {})}
        selectedCycles={selectedCycles}
        initialAboutSegments={initialAboutSegments}
        initialAskSegments={navState.askSegments || defaultAskSegments}
        selectedTopic={navState.topic}
        onCyclesChange={onCyclesSelectionChange}
        onAboutSegmentsChange={onAboutSegmentsSelectionChange}
        onAskSegmentsChange={onAskSegmentsSelectionChange}
        onTopicChange={onTopicSelectionChange}
        onSelfAssessmentChange={onSelfAssessmentChange}
        isSelfAssessmentIncluded={navState.isSelfAssessmentIncluded || false}
        surveySubjectType={state.navigation.surveySubjectType || SurveySubjectTypes.General}
        activeStepIndex={activeStepIndex}
      />
    </>
  ) : dataState.survey ? null : ( // Do not show placeholder if survey is selected even if controls aren't available
    <></>
  );

  return (
    <>
      <Header />
      <SpiderGraphSelector
        shouldHighlight={activeStepIndex === 0}
        urlParams={navState}
        activeSelect={state.navigation.surveySubjectType}
        activeSurveyId={state.navigation.survey}
        onSurveyChange={onSurveySelectChange}
      />
      {chartControls}
      <SpiderChart />
    </>
  );
};

export default SpiderGraph;
